/**
 * Update Checker
 *
 * Asks the server whether a newer build is live, and if so pulls it in and
 * reloads the page. Entirely silent -- no prompt, no banner, no "refresh now"
 * button. The user just ends up on the new build.
 *
 * WHY THIS EXISTS ALONGSIDE THE SERVICE WORKER
 *
 * Update delivery is still the service worker's job (SKIP_WAITING ->
 * controllerchange -> reload, in serviceWorkerRegistration.js). What this adds
 * is an independent source of truth for "is there a newer build". The worker's
 * own registration.update() only re-fetches service-worker.js, so we learn a
 * new build exists only as a side effect of the worker deciding one does.
 * /version.json is fetched with cache: 'no-store' straight from Firebase
 * Hosting, so the answer is unambiguous and cannot be masked by a stale worker
 * or a stale HTTP cache.
 *
 * It is also the recovery path when the worker is the thing that is broken: if
 * the page is uncontrolled, or the worker never produces a waiting replacement,
 * this falls back to a plain reload, which gets the new build because
 * index.html is served no-cache and the bundles are content-hashed.
 */

import { APP_VERSION } from '../version';

// Emitted into dist/ by webpack (see EmitVersionJsonPlugin in webpack.config.js)
// and excluded from the workbox precache, so it always comes off the network.
const VERSION_URL = '/version.json';

// Loop guard. If the new build somehow never lands -- a wedged worker serving
// an old precached index.html, say -- we would otherwise reload forever. Both
// keys live in sessionStorage: the cap is per tab, and closing/reopening gives
// it a fresh shot rather than blacklisting the device.
const TARGET_KEY = 'update_target_version';
const ATTEMPTS_KEY = 'update_reload_attempts';
const MAX_ATTEMPTS = 2;

// How long to let the service worker install + activate + claim the page before
// giving up on it and reloading ourselves. Generous on purpose: a full precache
// install on a slow shop connection is not fast, and reloading early lands us
// back on the old worker.
const SW_GRACE_MS = 20 * 1000;

// Same trigger shape as the worker's own update checks: on load, whenever the
// tab becomes visible (covers a PWA resumed from background), and on a timer
// (covers a counter iPad left open all day that never reloads).
const CHECK_INTERVAL_MS = 15 * 60 * 1000;
const CHECK_MIN_GAP_MS = 60 * 1000;

let lastCheck = 0;
let updating = false;
let started = false;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const readAttempts = (target) => {
  try {
    if (sessionStorage.getItem(TARGET_KEY) !== target) return 0;
    return parseInt(sessionStorage.getItem(ATTEMPTS_KEY) || '0', 10) || 0;
  } catch (e) {
    return 0;
  }
};

const recordAttempt = (target, attempts) => {
  try {
    sessionStorage.setItem(TARGET_KEY, target);
    sessionStorage.setItem(ATTEMPTS_KEY, String(attempts + 1));
  } catch (e) {
    // Private mode / storage full. Losing the guard is survivable; failing the
    // update because we could not write a counter is not.
  }
};

const clearAttempts = () => {
  try {
    sessionStorage.removeItem(TARGET_KEY);
    sessionStorage.removeItem(ATTEMPTS_KEY);
  } catch (e) {
    // ignore
  }
};

/**
 * Fetch the deployed build's version. Cache-busted twice over -- query string
 * and no-store -- because a cached answer here is worse than no answer.
 */
const fetchDeployedVersion = async () => {
  const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
    cache: 'no-store',
    credentials: 'omit',
  });
  if (!response.ok) {
    throw new Error(`version.json responded ${response.status}`);
  }
  const data = await response.json();
  if (!data || !data.version) {
    throw new Error('version.json has no "version" field');
  }
  return String(data.version);
};

/**
 * Resolve with the waiting worker once one exists, or null on timeout.
 *
 * registration.update() is supposed to settle after the new worker installs,
 * but browsers differ on exactly when, and a worker found by update() lands in
 * `installing` first. Watching for the state change is what makes this reliable
 * rather than a race we usually win.
 */
const waitForWaitingWorker = (registration, timeoutMs) =>
  new Promise((resolve) => {
    if (registration.waiting) {
      resolve(registration.waiting);
      return;
    }

    let settled = false;
    const finish = (worker) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      registration.removeEventListener('updatefound', onUpdateFound);
      resolve(worker);
    };

    const timer = setTimeout(() => finish(null), timeoutMs);

    function onUpdateFound() {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && registration.waiting) {
          finish(registration.waiting);
        }
        // 'redundant' means the install failed. Settle now so we fall through
        // to the plain-reload fallback instead of burning the grace period.
        if (installing.state === 'redundant') {
          finish(null);
        }
      });
    }

    registration.addEventListener('updatefound', onUpdateFound);
    onUpdateFound(); // an install may already be in flight
  });

/**
 * Pull the new build and reload into it.
 */
const applyUpdate = async (deployedVersion) => {
  const attempts = readAttempts(deployedVersion);
  if (attempts >= MAX_ATTEMPTS) {
    console.warn(
      `⚠️ Build ${deployedVersion} failed to land after ${attempts} attempts - not retrying this session`
    );
    return;
  }

  updating = true;
  recordAttempt(deployedVersion, attempts);
  console.log(
    `⬇️ New build ${deployedVersion} available (running ${APP_VERSION}) - updating...`
  );

  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker
      .getRegistration()
      .catch(() => null);

    if (registration) {
      await registration.update().catch(() => {});
      const waiting = await waitForWaitingWorker(registration, SW_GRACE_MS);

      if (waiting) {
        // Hand off. serviceWorkerRegistration.js reloads on 'controllerchange',
        // once the new worker has actually claimed the page -- reloading here
        // would race activation and can land us back on the old worker.
        waiting.postMessage({ type: 'SKIP_WAITING' });
        await delay(SW_GRACE_MS);
        // Still here means activation stalled, or nothing was listening for
        // controllerchange (that handler is skipped on a first install). Fall
        // through to the plain reload below.
        console.warn(
          '⚠️ Service worker did not take over in time - reloading directly'
        );
      }
    }
  }

  // No worker, no replacement worker, or it never claimed the page. index.html
  // is served no-cache and bundle filenames are content-hashed, so a plain
  // reload still lands on the new build unless a stale worker intercepts the
  // navigation -- which is what the attempt cap above exists to bound.
  window.location.reload();
};

/**
 * One check. Safe to call as often as you like; rate-limited internally.
 */
export const checkForUpdate = async ({ force = false } = {}) => {
  if (updating) return false;

  const now = Date.now();
  if (!force && now - lastCheck < CHECK_MIN_GAP_MS) return false;
  lastCheck = now;

  let deployedVersion;
  try {
    deployedVersion = await fetchDeployedVersion();
  } catch (error) {
    // Offline, or version.json is not deployed yet. Neither is actionable and
    // neither should be noisy on a flaky shop connection.
    return false;
  }

  if (deployedVersion === APP_VERSION) {
    // We are on the deployed build, so whatever attempt streak was in progress
    // succeeded. Reset it so a future deploy gets a full set of retries.
    clearAttempts();
    return false;
  }

  await applyUpdate(deployedVersion);
  return true;
};

/**
 * Start checking: immediately (this is the load/reload check), then whenever
 * the tab becomes visible, then on a timer for tabs that never do either.
 */
export const startUpdateChecks = () => {
  if (started) return;
  started = true;

  // force: the whole point is that every load asks the server.
  checkForUpdate({ force: true });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkForUpdate();
    }
  });

  setInterval(() => checkForUpdate(), CHECK_INTERVAL_MS);
};
